import React, { useEffect, useRef, useState } from 'react';
import { ClipboardList, Phone, CircleDashed } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

// How long a newly-arrived decision holds a brighter treatment before
// settling into the same look as the rest of the ticker (plan 03).
const NEW_ARRIVAL_HOLD_MS = 10_000;

function formatSimTime(ms: number): string {
  const totalMinutes = Math.floor(Math.max(0, ms) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `T+${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

// The moment the room realises the wall is watching them (plan 03). The
// packet is a full snapshot every 5s, not an incremental feed, so arrivals
// are detected by diffing eventIds against a ref of what's already been seen.
const DecisionTickerWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const tabletop = state?.tabletop;
  const decisions = tabletop?.recentDecisions ?? [];
  const openDecisions = tabletop?.openDecisions ?? [];

  const seenIds = useRef<Set<string>>(new Set());
  const isFirstRun = useRef(true);
  const pendingTimeouts = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set());

  // Clears any in-flight "fresh" highlight timers on unmount only — tying
  // this to the decision-watching effect below (which re-runs on every new
  // arrival) would cut an earlier decision's highlight short whenever a new
  // one lands inside its 10s window.
  useEffect(() => {
    return () => {
      pendingTimeouts.current.forEach((id) => clearTimeout(id));
      pendingTimeouts.current.clear();
    };
  }, []);

  useEffect(() => {
    // Seed seenIds from whatever's already in the packet on first mount —
    // otherwise every pre-existing decision (screen reload, widget added
    // mid-run) would get flagged as a "new arrival" and flash at once.
    if (isFirstRun.current) {
      isFirstRun.current = false;
      decisions.forEach((d) => seenIds.current.add(d.eventId));
      return;
    }

    const newlySeen = decisions.filter((d) => !seenIds.current.has(d.eventId));
    if (newlySeen.length === 0) return;

    newlySeen.forEach((d) => seenIds.current.add(d.eventId));
    setFreshIds((prev) => {
      const next = new Set(prev);
      newlySeen.forEach((d) => next.add(d.eventId));
      return next;
    });
    newlySeen.forEach((d) => {
      const timeoutId = setTimeout(() => {
        pendingTimeouts.current.delete(timeoutId);
        setFreshIds((prev) => {
          const next = new Set(prev);
          next.delete(d.eventId);
          return next;
        });
      }, NEW_ARRIVAL_HOLD_MS);
      pendingTimeouts.current.add(timeoutId);
    });
    // Only re-run when the set of decisions changes, not on every state tick
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions.map((d) => d.eventId).join(',')]);

  if (!tabletop) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No active exercise
      </div>
    );
  }

  if (decisions.length === 0 && openDecisions.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        Waiting on the room&apos;s first decision
      </div>
    );
  }

  // Oldest-first so the newest decision renders at the trailing (right) edge
  // and appears to push the earlier ones along, per the plan's ticker motion.
  const chronological = [...decisions].reverse();

  return (
    <div className="flex h-full px-3 py-2 gap-3">
      {openDecisions.length > 0 && (
        <div className={`flex-shrink-0 w-56 flex flex-col gap-1.5 overflow-y-auto border-r pr-3 ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          {openDecisions.map((d) => (
            <div key={d.branchId} className={`flex items-start gap-1.5 rounded-md px-2 py-1.5 flex-shrink-0 ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
              <CircleDashed size={14} className="mt-0.5 flex-shrink-0 text-amber-400" />
              <p className={`text-sm leading-snug ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{d.prompt}</p>
            </div>
          ))}
        </div>
      )}

      {chronological.length > 0 ? (
        <div className="flex-1 flex items-stretch gap-2 overflow-x-auto">
          {chronological.map((d) => {
            const isFresh = freshIds.has(d.eventId);
            return (
              <div
                key={d.eventId}
                className={`flex-shrink-0 w-64 flex flex-col justify-center gap-1 rounded-lg px-3 py-2 border transition-colors ${
                  isFresh ? 'border-primary bg-primary/15' : isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'
                }`}
                style={{ animation: 'decision-enter 0.4s ease-out' }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-base font-bold text-primary truncate">{d.displayName}</span>
                  <span className={`text-sm font-mono flex-shrink-0 ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{formatSimTime(d.simTimeMs)}</span>
                </div>
                <div className="flex items-start gap-1.5">
                  {d.type === 'call_log' ? (
                    <Phone size={14} className="mt-1 flex-shrink-0 text-primary" />
                  ) : (
                    <ClipboardList size={14} className="mt-1 flex-shrink-0 text-primary" />
                  )}
                  <p className={`text-xl leading-snug ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {d.type === 'call_log' ? `Called ${d.calledParty || 'someone'}` : d.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`flex-1 flex items-center justify-center text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
          No decisions logged yet
        </div>
      )}
    </div>
  );
};

export default DecisionTickerWidget;
